import React from 'react';
import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../redux/auth/selectors';
import GenericLineChart from '../../../charts/GenericLineChart';
import {
  HistoricalSrriData,
  generateSrriLineChartData,
} from './SyntheticRisk.data';
import makeStyles from '@mui/styles/makeStyles';
import GridItem from '../../../layout/GridComponents/GridItem';
import {
  percentageToNdecialPlaces,
  percentageToTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import CustomTable from '../../../tables/CustomTable';
import { RaptorTheme, mainColors } from '../../../../styling/theme';
import clsx from 'clsx';
import {
  Area,
  ComposedChart,
  Brush,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ExportButton from '../../../feedback/ExportButton';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { Box, Typography } from '@mui/material';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useSrriPageQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import {
  type SrriPageModel,
  type SrriPayload,
} from '../../../../api/rs-hg/risk/srri.types';

interface HistoricalNavChartProps {
  payload: SrriPayload;
}

const historicalChartStyles = makeStyles<RaptorTheme>(() => ({
  toolbar: {
    padding: '0.62rem',
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '1.56rem',
    color: mainColors.mainBlue,
    fontWeight: 300,
  },
  tooltip: {
    fontSize: '0.81rem',
    border: `1px solid ${mainColors.mainBlue}`,
  },
  brushContainer: {
    '& .recharts-surface': {
      overflow: 'visible',
    },
  },
}));

const HistoricalChart: React.FC<HistoricalNavChartProps> = ({ payload }) => {
  const classes = historicalChartStyles();

  const srriNavData = payload.time_series_data_v2;

  const getMinMaxVol = (srriNavData: any[]) => {
    let minVol = 1;
    let maxVol = 0;

    srriNavData.forEach((date) => {
      if (date.srri_volatility) {
        if (date.srri_volatility > maxVol) {
          maxVol = date.srri_volatility;
        }
        if (date.srri_volatility < minVol) {
          minVol = date.srri_volatility;
        }
      }
    });

    return { minVol: minVol, maxVol: maxVol };
  };

  const getDynamicRiskClassAreas = (srriNavData: any[]) => {
    const riskClassBoundaries = [
      [1, 0, 0.005],
      [2, 0.005, 0.02],
      [3, 0.02, 0.05],
      [4, 0.05, 0.1],
      [5, 0.1, 0.15],
      [6, 0.15, 0.25],
      [7, 0.25, 1],
    ];
    const minMaxVol = getMinMaxVol(srriNavData);
    const areas: any[] = [];

    const riskClasseIndexesToInclude: number[] = [];
    let i = 0;
    riskClassBoundaries.forEach((bound) => {
      if (minMaxVol.maxVol >= bound[1] && minMaxVol.minVol <= bound[2]) {
        riskClasseIndexesToInclude.push(i);
      }
      i++;
    });

    riskClasseIndexesToInclude.forEach((i) => {
      if (
        riskClassBoundaries[i][1] < minMaxVol.minVol &&
        riskClassBoundaries[i][2] > minMaxVol.maxVol
      ) {
        areas.push(
          <ReferenceArea
            ifOverflow="extendDomain"
            y1={minMaxVol.minVol - 0.005}
            y2={minMaxVol.maxVol + 0.005}
            stroke={mainColors.NA}
            strokeOpacity={0.3}
            label={`Risk Class ${riskClassBoundaries[i][0]}`}
            fill={'transparent'}
          />,
        );
      } else if (riskClassBoundaries[i][1] < minMaxVol.minVol) {
        areas.push(
          <ReferenceArea
            ifOverflow="extendDomain"
            y1={minMaxVol.minVol - 0.005}
            y2={riskClassBoundaries[i][2]}
            stroke={mainColors.NA}
            strokeOpacity={0.3}
            label={`Risk Class ${riskClassBoundaries[i][0]}`}
            fill={'transparent'}
          />,
        );
      } else if (riskClassBoundaries[i][2] > minMaxVol.maxVol) {
        areas.push(
          <ReferenceArea
            ifOverflow="extendDomain"
            y1={riskClassBoundaries[i][1]}
            y2={minMaxVol.maxVol + 0.005}
            stroke={mainColors.NA}
            strokeOpacity={0.3}
            label={`Risk Class ${riskClassBoundaries[i][0]}`}
            fill={'transparent'}
          />,
        );
      } else {
        areas.push(
          <ReferenceArea
            ifOverflow="extendDomain"
            y1={riskClassBoundaries[i][1]}
            y2={riskClassBoundaries[i][2]}
            stroke={mainColors.NA}
            strokeOpacity={0.3}
            label={`Risk Class ${riskClassBoundaries[i][0]}`}
            fill={'transparent'}
          />,
        );
      }
    });

    return areas;
  };

  const buildCsvData = (srriNavData: any[]) => {
    const datesToKeep: any[] = [];
    srriNavData.forEach((date) => {
      if (date.srri_volatility) {
        datesToKeep.push({
          date: date.date,
          nav: Number(date.nav).toFixed(4),
          srri_volatility: date.srri_volatility,
          published_srri_risk_class: date.published_srri_risk_class,
          calculated_srri_risk_class: date.calculated_srri_risk_class,
        });
      }
    });
    return datesToKeep;
  };

  const chartSyncId = 'navVsSrri';
  const chartMargin = { left: 0, bottom: 0, right: 50 };
  const yAxisWidth = 80;
  const xAxisProps = {
    dataKey: 'date',
    interval: 'preserveStartEnd' as const,
    minTickGap: 100,
  };

  return (
    <GridItem
      xs={12}
      cardStyle={{ padding: '0.62rem', paddingLeft: '0.94rem', overflow: 'visible' }}
      card
    >
      <Box className={classes.toolbar}>
        <Typography className={classes.title}>
          Historical Data ( NAV vs SRRI )
        </Typography>
        <Box>
          <ExportButton
            exportData={buildCsvData(srriNavData)}
            pdfIdentifier={'id'}
            fields={[
              'date',
              'nav',
              'srri_volatility',
              'published_srri_risk_class',
              'calculated_srri_risk_class',
            ]}
            fileName={`SRRI vs NAV`}
            allowPdfExport={false}
          />
        </Box>
      </Box>
      <ResponsiveContainer width={'100%'} height={400}>
        <LineChart
          data={srriNavData}
          syncId={chartSyncId}
          margin={{ ...chartMargin, top: 30 }}
        >
          <XAxis {...xAxisProps} />
          <YAxis
            domain={[
              (dataMin: number) => dataMin - Math.abs(dataMin * 0.01),
              (dataMax: number) => dataMax + Math.abs(dataMax * 0.01),
            ]}
            interval={'preserveStartEnd'}
            scale="linear"
            allowDataOverflow={false}
            orientation={'left'}
            width={yAxisWidth}
            tickFormatter={(v: number) => Number(v).toFixed(2)}
          />
          <Tooltip
            wrapperClassName={classes.tooltip}
            formatter={(value, name) => {
              if (name === 'nav') {
                return [Number(value).toFixed(4), 'NAV'];
              } else {
                return [value, name];
              }
            }}
          />
          <Line
            type="monotone"
            dataKey={'nav'}
            stroke={mainColors.mainBlue}
            fill={mainColors.mainBlue}
            dot={{ stroke: 'transparent', strokeWidth: 2 }}
            activeDot={{ stroke: mainColors.mainBlue, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
      <ResponsiveContainer width={'100%'} height={150}>
        <ComposedChart
          data={srriNavData}
          syncId={chartSyncId}
          margin={{ ...chartMargin, top: 0 }}
        >
          <XAxis {...xAxisProps} />
          <YAxis
            domain={[
              (dataMin: number) => dataMin,
              (dataMax: number) => dataMax,
            ]}
            interval={'preserveStartEnd'}
            scale="linear"
            allowDataOverflow={false}
            orientation={'left'}
            width={yAxisWidth}
          />
          <Tooltip
            wrapperClassName={classes.tooltip}
            formatter={(value, name) => {
              if (name === 'srri_volatility') {
                return [Number(value).toFixed(4), 'SRRI'];
              } else {
                return [value, name];
              }
            }}
            active={false}
          />
          <Area
            connectNulls
            type="monotone"
            dataKey={'srri_volatility'}
            stroke={mainColors.mainBlue}
            fill={'transparent'}
            strokeWidth={2}
            dot={{ stroke: 'transparent', strokeWidth: 2 }}
            activeDot={{ stroke: mainColors.mainBlue, strokeWidth: 2 }}
          />

          {getDynamicRiskClassAreas(srriNavData)}
        </ComposedChart>
      </ResponsiveContainer>
      <div className={classes.brushContainer}>
      <ResponsiveContainer width={'100%'} height={50}>
        <LineChart
          data={srriNavData}
          syncId={chartSyncId}
          margin={{ ...chartMargin, top: 0, left: yAxisWidth }}
        >
          <Line
            type="monotone"
            dataKey="nav"
            stroke="transparent"
            dot={false}
            activeDot={false}
          />
          <Brush
            dataKey="date"
            height={15}
            stroke={mainColors.mainBlue_lightAlt}
            style={{ fontWeight: 100 }}
          />
        </LineChart>
      </ResponsiveContainer>
      </div>
    </GridItem>
  );
};

const statusBoxStyles = makeStyles<RaptorTheme>(() => ({
  statusBoxContainer: {
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  title: {
    fontSize: '1.56rem',
    color: mainColors.mainBlue,
    fontWeight: 300,
  },
  levelsBox: {
    display: 'flex',
    gap: '1.25rem',
  },
  levelIndicator: {
    flexGrow: 1,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '0.62rem',
  },
  generalHeader: {
    height: '1.88rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.62rem 0.62rem 0 0',
    color: 'white',
    fontSize: '0.94rem',
    fontWeight: 500,
  },
  generalActiveHeader: {
    borderRight: `1px solid ${mainColors.NA_darker}`,
    borderTop: `1px solid ${mainColors.NA_darker}`,
    borderLeft: `1px solid ${mainColors.NA_darker}`,
  },
  passHeader: {
    backgroundColor: mainColors.Pass,
  },
  failExpectedHeader: {
    backgroundColor: mainColors.NA,
  },
  failActualHeader: {
    backgroundColor: mainColors.Fail,
  },
  indicatorBody: {
    padding: '1.25rem 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
    border: `1px solid ${mainColors.NA_darker}`,
  },
  levelLabel: {
    color: mainColors.mainBlue,
    fontWeight: 600,
  },
  boundariesLabel: {
    fontSize: '0.75rem',
    color: mainColors.mainBlue_lighter,
  },
}));

const SrriStatusBox: React.FC<{ payload: SrriPayload }> = ({ payload }) => {
  const classes = statusBoxStyles();

  const getIndicatorHeaderStyling = (
    key: string,
    itemLimits: string[],
    chosenLevel: string,
    weeklyVol: number,
  ) => {
    if (key === '7') {
      if (
        chosenLevel === key &&
        weeklyVol >= Number(itemLimits[0].split('%')[0]) &&
        weeklyVol < 100
      ) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.passHeader,
        );
      } else if (chosenLevel === key && weeklyVol) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.failExpectedHeader,
        );
      } else if (
        weeklyVol >= Number(itemLimits[0].split('%')[0]) &&
        weeklyVol < 100
      ) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.failActualHeader,
        );
      } else {
        return classes.generalHeader;
      }
    } else {
      if (
        chosenLevel === key &&
        weeklyVol >= Number(itemLimits[0].split('%')[0]) &&
        weeklyVol < Number(itemLimits[1].split('%')[0])
      ) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.passHeader,
        );
      } else if (chosenLevel === key && weeklyVol) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.failExpectedHeader,
        );
      } else if (
        weeklyVol >= Number(itemLimits[0].split('%')[0]) &&
        weeklyVol < Number(itemLimits[1].split('%')[0])
      ) {
        return clsx(
          classes.generalHeader,
          classes.generalActiveHeader,
          classes.failActualHeader,
        );
      } else {
        return classes.generalHeader;
      }
    }
  };

  const getIndicatorHeaderLabel = (
    key: string,
    itemLimits: string[],
    chosenLevel: string,
    weeklyVol: number,
  ) => {
    if (key === '7') {
      if (
        chosenLevel === key &&
        weeklyVol >= Number(itemLimits[0].split('%')[0]) &&
        weeklyVol < 100
      ) {
        return 'PASS';
      } else if (chosenLevel === key && weeklyVol) {
        return 'PUBLISHED';
      } else if (
        weeklyVol >= Number(itemLimits[0].split('%')[0]) &&
        weeklyVol < 100
      ) {
        return 'ACTUAL';
      } else {
        return '';
      }
    } else {
      if (
        chosenLevel === key &&
        weeklyVol >= Number(itemLimits[0].split('%')[0]) &&
        weeklyVol < Number(itemLimits[1].split('%')[0])
      ) {
        return 'PASS';
      } else if (chosenLevel === key && weeklyVol) {
        return 'PUBLISHED';
      } else if (
        weeklyVol >= Number(itemLimits[0].split('%')[0]) &&
        weeklyVol < Number(itemLimits[1].split('%')[0])
      ) {
        return 'ACTUAL';
      } else {
        return '';
      }
    }
  };

  return (
    <GridItem xs={12} card className={classes.statusBoxContainer}>
      <Typography className={classes.title}>SRRI Status</Typography>
      <Box className={classes.levelsBox}>
        {Object.keys(payload.srri_data).map((key) => {
          return (
            <Box key={key} className={classes.levelIndicator}>
              <Box
                className={getIndicatorHeaderStyling(
                  key,
                  payload.srri_data[key],
                  String(payload.limits[2]),
                  payload.weekly_volatility,
                )}
              >
                {getIndicatorHeaderLabel(
                  key,
                  payload.srri_data[key],
                  String(payload.limits[2]),
                  payload.weekly_volatility,
                )}
              </Box>
              <Box className={classes.indicatorBody}>
                <Typography className={classes.levelLabel}>
                  Risk Class {key}
                </Typography>
                <Typography className={classes.boundariesLabel}>
                  {`${payload.srri_data[key][0]} <= SRRI < ${payload.srri_data[key][1]}`}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </GridItem>
  );
};

const formatName = (el: string) => {
  const spaced = el.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.includes(' ')
    ? spaced.charAt(0).toUpperCase() + spaced.slice(1)
    : el.toUpperCase();
};

const HistoricalGraphics: React.FC<{ payload: SrriPayload }> = ({ payload }) => {
  const chartData = generateSrriLineChartData(payload);
  const tableData = [...chartData];
  // Get the name user id
  const clientName = useSelector(clientNameSelector);
  // Create the formatter for the srri value based on the user id
  const formatter = clientName?.includes('coutts')
    ? (num: number | string) => percentageToNdecialPlaces(num, 9)
    : (num: number | string) => percentageToNdecialPlaces(num, 4);

  tableData.reverse();
  return (
    <>
      <GridItem
        xs={8}
        card
        style={{ alignSelf: 'stretch' }}
        cardStyle={{ padding: '1.25rem', height: '100%' }}
      >
        <GenericLineChart
          title="Historical SRRI - Chart"
          margin={{ top: 20, left: 0, right: 40, bottom: 20 }}
          error={null}
          yAxisDomain={[
            (dataMin: number) => dataMin - Math.abs(dataMin * 0.1),
            (dataMax: number) => dataMax + Math.abs(dataMax * 0.05),
          ]}
          showTitle
          width="100%"
          height={600}
          data={chartData}
          lines={[
            { dataKey: 'upperBound', color: mainColors.Fail },
            { dataKey: 'lowerBound', color: mainColors.Alert },
            { dataKey: 'srri', color: mainColors.mainBlue },
          ]}
          tooltipFormatter={(value: number, name: string) => {
            return [percentageToTwoDecimalPlaces(value), formatName(name)];
          }}
          xAxisDataKey="date"
          yAxisTickFormatter={(value: number) =>
            percentageToTwoDecimalPlaces(value)
          }
        />
      </GridItem>
      <GridItem card xs={4}>
        <CustomTable<HistoricalSrriData>
          data={tableData}
          columns={['date', 'lowerBound', 'srri', 'upperBound'].map((el) => ({
            field: el,
            title: formatName(el),
            headerStyle: {
              textAlign: 'center',
            },
            cellStyle: {
              textAlign: 'center',
            },
            render: (rowData: HistoricalSrriData) =>
              el !== 'date'
                ? formatter(rowData[el as keyof HistoricalSrriData])
                : rowData['date'],
          }))}
          options={{ paging: true, pageSize: 10, exportButton: true }}
          showToolbar
          title="Historical SRRI - Table"
          toolbarComponents={{
            titleStyle: {
              fontWeight: 300,
            },
          }}
        />
      </GridItem>
    </>
  );
};

export const SyntheticRiskComponents: React.FC<{ payload: SrriPayload }> = ({
  payload,
}) => {
  return payload && typeof payload === 'object' && !Array.isArray(payload) ? (
    <>
      <HistoricalChart payload={payload} />
      <SrriStatusBox payload={payload} />
      <HistoricalGraphics payload={payload} />
    </>
  ) : (
    <NoDataMessage message={'No Data Available For This Share Class'} />
  );
};

interface SyntheticRiskContentProps {
  fundId: string;
  gmidShareclass?: string;
}

export const SyntheticRiskContent: React.FC<SyntheticRiskContentProps> = ({
  fundId,
  gmidShareclass,
}) => {
  const srriQuery = useSrriPageQuery(fundId, gmidShareclass, {
    enabled: gmidShareclass === undefined ? true : !!gmidShareclass,
  });

  return (
    <QueryBoundary<SrriPageModel>
      isLoading={srriQuery.isLoading}
      isFetching={srriQuery.isFetching}
      isError={srriQuery.isError}
      error={srriQuery.error}
      data={
        srriQuery.data?.data &&
        typeof srriQuery.data.data === 'object' &&
        !Array.isArray(srriQuery.data.data)
          ? srriQuery.data
          : null
      }
      loadingMessages={['Loading SRRI Data', 'Just a few more seconds...']}
      errorMessage="No SRRI Data Available."
      noDataMessage="No SRRI Data Available."
    >
      {(pageModel) => <SyntheticRiskComponents payload={pageModel.data} />}
    </QueryBoundary>
  );
};

const SyntheticRisk = (props: FundInfoComponentProps) => {
  return <SyntheticRiskContent fundId={props.fundId} />;
};
export default SyntheticRisk;
